/**
 * B2BDiscoveryPanel
 *
 * Collapsible panel above the Leads list that lets the admin:
 *
 *   1. Preview Bundesagentur results for a (keyword, location) combo
 *   2. Trigger Bundesagentur company discovery (background)
 *   3. Trigger the full discovery chain (BA -> jobs -> classify -> contacts)
 *   4. Watch live run status (poll every 4s while any run is queued/running)
 *
 * Mounts a small in-component poller so we get live status without changing
 * parent state. Calls `onCompaniesChanged()` when a run finishes successfully
 * so the leads list reloads.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Play, ChevronDown, ChevronUp, Eye, CheckCircle2,
  AlertTriangle, Clock, Radar, Layers, Database, Trash2, Bookmark, BookmarkPlus,
} from 'lucide-react';

import type { ApiCall } from '../../api/interview';
import {
  previewDiscovery, triggerBundesagentur, triggerFullChain,
  cleanupJobBoards, listDiscoveryRuns, uploadCompaniesCSV,
  type DiscoveryRun, type DiscoveryPreview, type JobBoardCleanupResult,
} from '../../api/b2b';
import { DACH_SEED_COMPANIES, buildSeedCsvFile } from './seed-dach-companies';
import {
  DEFAULT_DISCOVERY_PRESETS,
  deleteDiscoveryPreset,
  listDiscoveryPresets,
  saveDiscoveryPreset,
  type DiscoveryPreset,
} from './discovery_presets';
import { LoadingNudge, HeadingWithInfo, InfoTip, ActionWithInfo } from './_ui';

interface Props {
  apiCall: ApiCall;
  onCompaniesChanged: () => void;
}

const DEFAULT_KEYWORDS = [
  'Software Engineer',
  'Backend Developer',
  'Frontend Developer',
  'Full Stack Developer',
  'DevOps Engineer',
  'Data Engineer',
  'Machine Learning Engineer',
  'Site Reliability Engineer',
  'Cloud Engineer',
  'Mobile Developer',
];

const LOCATIONS = [
  'Deutschland', 'Berlin', 'München', 'Hamburg', 'Köln', 'Frankfurt',
  'Stuttgart', 'Wien', 'Zürich',
];

const B2BDiscoveryPanel: React.FC<Props> = ({ apiCall, onCompaniesChanged }) => {
  const [expanded, setExpanded] = useState(true);

  // Form state
  const [previewKeyword, setPreviewKeyword] = useState('Software Engineer');
  const [location, setLocation] = useState('Deutschland');
  const [sinceDays, setSinceDays] = useState(14);
  const [maxPages, setMaxPages] = useState(3);
  const [keywordsCSV, setKeywordsCSV] = useState(DEFAULT_KEYWORDS.join(', '));

  // Chain options
  const [runJobs, setRunJobs] = useState(true);
  const [runClassify, setRunClassify] = useState(true);
  const [runContacts, setRunContacts] = useState(true);

  // UI state
  const [busy, setBusy] = useState<null | 'preview' | 'discover' | 'chain' | 'seed' | 'cleanup'>(null);
  const [busyDetail, setBusyDetail] = useState<string | null>(null);
  const [preview, setPreview] = useState<DiscoveryPreview | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Presets (localStorage)
  const [presets, setPresets] = useState<DiscoveryPreset[]>(() => listDiscoveryPresets());
  const [presetName, setPresetName] = useState('');

  // Cleanup preview
  const [cleanupPreview, setCleanupPreview] = useState<JobBoardCleanupResult | null>(null);

  // Runs polling
  const [runs, setRuns] = useState<DiscoveryRun[]>([]);

  const loadRuns = useCallback(async () => {
    try {
      const data = await listDiscoveryRuns(apiCall);
      setRuns(data);
    } catch {
      // silent — polling failures are non-fatal
    }
  }, [apiCall]);

  // Initial load + poll
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => {
    const active = runs.some((r) => r.status === 'queued' || r.status === 'running');
    if (!active) return;
    const id = setInterval(loadRuns, 4000);
    return () => clearInterval(id);
  }, [runs, loadRuns]);

  // Reload companies list when any run transitions to done
  const doneIds = useMemo(
    () => runs.filter((r) => r.status === 'done').map((r) => r.id).join(','),
    [runs],
  );
  useEffect(() => {
    if (doneIds) onCompaniesChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneIds]);

  const parsedKeywords = useMemo(
    () => keywordsCSV.split(',').map((s) => s.trim()).filter(Boolean),
    [keywordsCSV],
  );

  const applyPreset = (p: Omit<DiscoveryPreset, 'id' | 'createdAt'>) => {
    setKeywordsCSV(p.keywordsCSV);
    setLocation(p.location);
    setSinceDays(p.sinceDays);
    setMaxPages(p.maxPages);
    setPreviewKeyword(p.previewKeyword);
    setFeedback(`Loaded preset: ${p.name}`);
  };

  const handleSavePreset = () => {
    const name = presetName.trim() || `${location} · ${previewKeyword}`;
    const saved = saveDiscoveryPreset({
      name,
      keywordsCSV,
      location,
      sinceDays,
      maxPages,
      previewKeyword,
    });
    setPresets(listDiscoveryPresets());
    setPresetName('');
    setFeedback(`Saved preset "${saved.name}"`);
  };

  const handleDeletePreset = (id: string) => {
    deleteDiscoveryPreset(id);
    setPresets(listDiscoveryPresets());
  };

  const doPreview = async () => {
    setBusy('preview');
    setBusyDetail(`Querying Bundesagentur for "${previewKeyword}" in ${location}…`);
    setError(null);
    setFeedback(null);
    try {
      const p = await previewDiscovery(apiCall, {
        keyword: previewKeyword,
        location,
        since_days: sinceDays,
      });
      setPreview(p);
    } catch (e: any) {
      setError(e?.message || 'Preview failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const doDiscover = async () => {
    setBusy('discover');
    setBusyDetail(
      `Starting BA discovery: ${parsedKeywords.length} keywords × ${maxPages} pages in ${location}…`,
    );
    setError(null);
    setFeedback(null);
    try {
      const r = await triggerBundesagentur(apiCall, {
        keywords: parsedKeywords,
        location,
        max_pages_per_keyword: maxPages,
        since_days: sinceDays,
      });
      setFeedback(`Bundesagentur discovery started (run ${r.run_id.slice(0, 8)}…)`);
      await loadRuns();
    } catch (e: any) {
      setError(e?.message || 'Discovery failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const doLoadSeed = async () => {
    setBusy('seed');
    setBusyDetail(`Uploading ${DACH_SEED_COMPANIES.length} curated DACH companies to database…`);
    setError(null);
    setFeedback(null);
    try {
      const file = buildSeedCsvFile();
      const r = (await uploadCompaniesCSV(apiCall, file)) as any;
      setFeedback(
        `Curated DACH seed loaded: +${r.added} new, ${r.skipped_duplicates} already in DB, ${r.skipped_invalid} invalid. ` +
        `ATS will be auto-detected on next 'Discover companies' or 'Run full chain'.`,
      );
      onCompaniesChanged();
    } catch (e: any) {
      setError(e?.message || 'Seed load failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const doFullChain = async () => {
    setBusy('chain');
    setBusyDetail('Starting full chain: BA → discover jobs → classify → contacts (2-5 min)…');
    setError(null);
    setFeedback(null);
    try {
      const r = await triggerFullChain(apiCall, {
        bundesagentur: {
          keywords: parsedKeywords,
          location,
          max_pages_per_keyword: maxPages,
          since_days: sinceDays,
        },
        run_discover_jobs: runJobs,
        run_classify_jobs: runClassify,
        run_discover_contacts: runContacts,
        run_generate_drafts: false,
      });
      setFeedback(`Full chain started (run ${r.run_id.slice(0, 8)}…). This can take a few minutes.`);
      await loadRuns();
    } catch (e: any) {
      setError(e?.message || 'Chain trigger failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const doCleanupScan = async () => {
    setBusy('cleanup');
    setBusyDetail('Scanning DB for companies whose domain is a known job-board host…');
    setError(null);
    try {
      const r = await cleanupJobBoards(apiCall, true);
      setCleanupPreview(r);
      if ((r.total_candidates ?? 0) === 0) {
        setFeedback('No job-board pollution found - list looks clean.');
      }
    } catch (e: any) {
      setError(e?.message || 'Cleanup scan failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const doCleanupDelete = async () => {
    if (!cleanupPreview?.total_candidates) return;
    setBusy('cleanup');
    setBusyDetail(`Deleting ${cleanupPreview.total_candidates} polluted job-board entries…`);
    setError(null);
    try {
      const r = await cleanupJobBoards(apiCall, false);
      setCleanupPreview(null);
      setFeedback(`Removed ${r.deleted_count ?? 0} job-board pollution entries.`);
      onCompaniesChanged();
    } catch (e: any) {
      setError(e?.message || 'Cleanup delete failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const busyTitle = (() => {
    switch (busy) {
      case 'preview': return 'Previewing Bundesagentur results…';
      case 'discover': return 'Starting Bundesagentur discovery…';
      case 'chain': return 'Starting full discovery chain…';
      case 'seed': return 'Loading curated seed list…';
      case 'cleanup': return 'Cleaning up job-board pollution…';
      default: return null;
    }
  })();

  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-3 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Radar className="w-4 h-4 text-pink-600" />
          <HeadingWithInfo
            info="Neue Firmen finden und importieren - entweder kuratierte Seed-Liste oder live aus der Bundesagentur-Stellenbörse."
          >
            Lead Discovery
          </HeadingWithInfo>
          <span className="text-xs font-normal text-slate-400">
            (Bundesagentur · Seed)
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-3 py-3 space-y-3 text-sm">
          {/* Curated seed shortcut */}
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 flex items-center gap-3">
            <Database className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="flex-1 text-xs text-amber-900">
              <span className="font-medium">{DACH_SEED_COMPANIES.length} curated DACH companies</span>
              {' '} -  synced from Company Explore + hand-picked targets. Load seed, then detect ATS per company.
            </div>
            <button
              type="button"
              onClick={doLoadSeed}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700 disabled:opacity-50"
            >
              {busy === 'seed' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
              Load seed
            </button>
            <InfoTip
              align="right"
              text="Importiert die kuratierte DACH-Liste (Domains bekannt, hohe Qualität). Danach Pipeline workers starten."
            />
          </div>

          <div className="text-xs text-slate-400 -mt-1 mb-1"> -  or - discover live from Bundesagentur:</div>

          {/* Saved BA search presets */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2 space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
              <Bookmark className="w-3.5 h-3.5" />
              <HeadingWithInfo info="Gespeicherte BA-Suchparameter (Keywords, Ort, Tage). Schnell zwischen Tech-Rollen wechseln.">
                Saved BA searches
              </HeadingWithInfo>
            </div>
            <div className="flex flex-wrap gap-1">
              {DEFAULT_DISCOVERY_PRESETS.map((p, i) => (
                <button
                  key={`default-${i}`}
                  type="button"
                  onClick={() => applyPreset(p)}
                  disabled={busy !== null}
                  className="px-2 py-0.5 text-[11px] rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50"
                >
                  {p.name}
                </button>
              ))}
              {presets.map((p) => (
                <span key={p.id} className="inline-flex items-center">
                  <button
                    type="button"
                    onClick={() => applyPreset(p)}
                    disabled={busy !== null}
                    className="px-2 py-0.5 text-[11px] rounded-l border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50"
                  >
                    {p.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeletePreset(p.id)}
                    disabled={busy !== null}
                    className="px-1 py-0.5 text-[11px] rounded-r border border-l-0 border-slate-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
                    title="Delete preset"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Preset name (optional)"
                className="flex-1 text-xs border border-slate-200 rounded px-2 py-1"
              />
              <button
                type="button"
                onClick={handleSavePreset}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded bg-white hover:bg-slate-100 disabled:opacity-50"
              >
                <BookmarkPlus className="w-3.5 h-3.5" />
                Save current
              </button>
              <InfoTip text="Speichert aktuelle Keywords, Location, Since days und Pages als Preset (localStorage)." align="right" />
            </div>
          </div>

          {/* Configuration row */}
          <div className="flex items-center gap-1 text-xs text-slate-600 mb-1">
            <span className="font-medium">BA search params</span>
            <InfoTip text="Bundesagentur-Stellenbörse: Ort, Zeitraum, Seiten pro Keyword. Domain wird aus Arbeitgeber-Namen geraten." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Location</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5"
              >
                {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Since (days)</label>
              <input
                type="number"
                min={1} max={90}
                value={sinceDays}
                onChange={(e) => setSinceDays(Math.max(1, Math.min(90, Number(e.target.value) || 14)))}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Pages / keyword</label>
              <input
                type="number"
                min={1} max={20}
                value={maxPages}
                onChange={(e) => setMaxPages(Math.max(1, Math.min(20, Number(e.target.value) || 3)))}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5"
              />
              <div className="text-[10px] text-slate-400 mt-0.5">
                100 jobs/page · {parsedKeywords.length} keywords ≈ {parsedKeywords.length * maxPages * 100} max calls
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Preview keyword</label>
              <input
                type="text"
                value={previewKeyword}
                onChange={(e) => setPreviewKeyword(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Keywords (comma-separated)
            </label>
            <textarea
              value={keywordsCSV}
              onChange={(e) => setKeywordsCSV(e.target.value)}
              rows={2}
              className="w-full border border-slate-200 rounded-md px-2 py-1.5 font-mono text-xs"
            />
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-slate-600">
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={runJobs} onChange={(e) => setRunJobs(e.target.checked)} />
              + discover ATS jobs
              <InfoTip text="Nach BA-Import: ATS erkennen und Stellen holen (wie Pipeline › Discover jobs)." />
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={runClassify} onChange={(e) => setRunClassify(e.target.checked)} />
              + classify jobs
              <InfoTip text="Jobs per LLM kategorisieren + ICP-Tier setzen." />
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={runContacts} onChange={(e) => setRunContacts(e.target.checked)} />
              + discover contacts
              <InfoTip text="Emails aus Impressum/Team scrapen - nicht LinkedIn-Leaders." />
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 items-center">
            <ActionWithInfo
              info="Zeigt Vorschau wie viele Arbeitgeber BA liefert - schreibt nichts in die DB."
            >
              <button
                type="button"
                onClick={doPreview}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-md text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {busy === 'preview' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Preview
              </button>
            </ActionWithInfo>
            <ActionWithInfo
              info="Sucht Stellen bei der Bundesagentur, rät Domain aus Firmennamen, legt neue Firmen an (Dupes werden übersprungen)."
            >
              <button
                type="button"
                onClick={doDiscover}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:opacity-50"
              >
                {busy === 'discover' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Discover companies
              </button>
            </ActionWithInfo>
            <ActionWithInfo
              info="Alles hintereinander: BA → Jobs → Classify → Contacts. Dauert mehrere Minuten - nicht parallel zu Pipeline workers."
            >
              <button
                type="button"
                onClick={doFullChain}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
              >
                {busy === 'chain' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                Run full chain
              </button>
            </ActionWithInfo>
          </div>

          {busy && busyTitle && (
            <LoadingNudge title={busyTitle} step={busyDetail || undefined} />
          )}

          {feedback && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-2 py-1.5">
              {feedback}
            </div>
          )}
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 text-red-800 text-xs px-2 py-1.5">
              {error}
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div className="font-medium text-slate-700 mb-1">
                BA returned ~{preview.ba_total_jobs.toLocaleString()} total jobs;
                {' '}page sample has <strong>{preview.unique_employers_in_page}</strong> unique employers
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                {preview.sample.map((s, i) => (
                  <div key={i} className="py-1 flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-700 truncate">{s.employer}</div>
                      <div className="text-slate-500 truncate">
                        {s.title_sample || '-'}
                        {s.location ? ` · ${s.location}` : ''}
                      </div>
                    </div>
                    {s.external_url && (
                      <a href={s.external_url} target="_blank" rel="noreferrer"
                         className="text-pink-600 text-[10px] underline truncate max-w-[140px]">
                        link
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Job-board cleanup */}
          <div className="rounded-md border border-red-100 bg-red-50/50 p-2 flex flex-wrap items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-600 shrink-0" />
            <div className="flex-1 text-xs text-red-900 min-w-[200px]">
              Remove polluted entries (job-board domains like jobware.de, persy.jobs from early BA runs).
            </div>
            <button
              type="button"
              onClick={doCleanupScan}
              disabled={busy !== null}
              className="px-2 py-1 text-xs border border-red-200 rounded bg-white hover:bg-red-50 disabled:opacity-50"
            >
              Scan pollution
            </button>
            <InfoTip
              align="right"
              text="Entfernt fälschlich importierte Job-Board-Domains (jobware.de etc.) aus frühen BA-Läufen."
            />
            {cleanupPreview && (cleanupPreview.total_candidates ?? 0) > 0 && (
              <button
                type="button"
                onClick={doCleanupDelete}
                disabled={busy !== null}
                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                Delete {cleanupPreview.total_candidates} entries
              </button>
            )}
          </div>
          {cleanupPreview && (cleanupPreview.candidates?.length ?? 0) > 0 && (
            <div className="text-[11px] text-slate-500 max-h-24 overflow-y-auto border border-slate-100 rounded p-2">
              {cleanupPreview.candidates!.slice(0, 15).map((c) => (
                <div key={c.id}>{c.domain} · {c.name || '-'} · {c.source}</div>
              ))}
              {(cleanupPreview.total_candidates ?? 0) > 15 && (
                <div>…and {(cleanupPreview.total_candidates ?? 0) - 15} more</div>
              )}
            </div>
          )}

          {/* Runs */}
          {runs.length > 0 && (
            <div className="rounded-md border border-slate-200 overflow-hidden">
              <div className="px-3 py-1.5 bg-slate-50 text-xs font-medium text-slate-600 border-b border-slate-100 flex items-center gap-1">
                <HeadingWithInfo
                  info="Status laufender/fertiger Tasks. Geht bei Deploy verloren - Ergebnisse stehen in der Firmenliste (Jobs, Contacts)."
                >
                  Recent runs
                </HeadingWithInfo>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                {runs.slice(0, 8).map((r) => <RunRow key={r.id} run={r} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default B2BDiscoveryPanel;

// ============================================================
// Sub-components
// ============================================================

const RunRow: React.FC<{ run: DiscoveryRun }> = ({ run }) => {
  const icon = (() => {
    switch (run.status) {
      case 'done':    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
      case 'error':   return <AlertTriangle className="w-3.5 h-3.5 text-red-600" />;
      case 'running': return <Loader2 className="w-3.5 h-3.5 text-pink-600 animate-spin" />;
      default:        return <Clock className="w-3.5 h-3.5 text-slate-400" />;
    }
  })();

  const stats = run.stats || {};
  const summary = (() => {
    if (run.kind === 'bundesagentur') {
      const ins = stats.inserted ?? 0;
      const dup = stats.duplicates ?? 0;
      const uniq = stats.unique_employers ?? 0;
      return `+${ins} new · ${dup} dupes · ${uniq} employers`;
    }
    if (run.kind === 'ats_slugs') {
      const ins = stats.inserted ?? 0;
      const valid = stats.valid_with_jobs ?? 0;
      const total = stats.total_slugs ?? 0;
      return `+${ins} new · ${valid}/${total} valid slugs`;
    }
    if (run.kind === 'full_chain') {
      const ba = stats.bundesagentur || {};
      return `BA +${ba.inserted ?? 0} · ${Object.keys(stats).length} stages`;
    }
    if (run.kind === 'discover_jobs') {
      return `exit ${stats.exit_code ?? '?'} · ${Math.round(stats.duration_s ?? 0)}s`;
    }
    if (run.kind === 'classify_jobs') {
      const icp = stats.icp || {};
      return `exit ${stats.exit_code ?? '?'} · ICP hot=${icp.set_hot ?? 0} warm=${icp.set_warm ?? 0}`;
    }
    if (run.kind === 'score_icp') {
      return `hot=${stats.set_hot ?? 0} warm=${stats.set_warm ?? 0} cold=${stats.set_cold ?? 0}`;
    }
    if (run.kind === 'generate_drafts' || run.kind === 'send_batch' || run.kind === 'poll_replies') {
      return `exit ${stats.exit_code ?? '?'} · campaign ${stats.campaign_id ?? '-'}`;
    }
    if (run.kind === 'discover_contacts') {
      return `exit ${stats.exit_code ?? '?'}`;
    }
    if (run.kind === 'discover_decision_makers') {
      return `+${stats.contacts_added ?? 0} leaders · ${stats.companies_no_candidates ?? 0} empty · ${Math.round(stats.duration_s ?? 0)}s`;
    }
    return JSON.stringify(stats).slice(0, 60);
  })();

  const liveLog = run.status === 'running' && run.log?.length
    ? run.log[run.log.length - 1]
    : null;

  return (
    <div className="px-3 py-1.5 text-xs flex items-center gap-2">
      <span>{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-slate-700 truncate">
          <span className="font-mono">{run.id.slice(0, 8)}</span>
          {' · '}
          <span className="capitalize">{run.kind.replace(/_/g, ' ')}</span>
          {run.params?.location ? ` · ${run.params.location}` : ''}
          {run.params?.ats ? ` · ${run.params.ats}` : ''}
        </div>
        <div className="text-slate-500 truncate">
          {run.status === 'error' ? (run.error || 'error') : (liveLog || summary)}
        </div>
      </div>
      <div className="text-slate-400 tabular-nums">
        {run.duration_s ? `${Math.round(run.duration_s)}s` : ''}
      </div>
    </div>
  );
};
